import React, { useMemo, useCallback } from "react";
import classNames from "classnames";
import { formatDecimal } from "@/utils/number/formatters";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { AggregateTooltipContent } from "./AggregateTooltipContent";
import type { AggregateData } from "./AggregateTooltipContent";
import type { AggregateRowProps, ColorState } from "./types";
import styles from "./AggregateRow.module.css";

/**
 * AggregateRow - Displays booking statistics with percentage, progress bar, and total hours.
 * Shows color-coded status based on booking percentage.
 *
 * Responsive behavior (via CSS container queries):
 * - Horizontal (>280px): All elements inline [100%] [████░░░] [40 T]
 * - Compact (180-280px): Stats row above full-width progress bar
 * - Mini (<180px): Single combined badge showing "40/40 T"
 *
 * Color states and thresholds:
 * - Grey: 0% - Ingen bookinger
 * - Orange: 1-71% - Delvis booket
 * - Green: 72-100% - Fullt booket
 * - Red: >100% - Overbooket
 */
export const AggregateRow: React.FC<AggregateRowProps> = ({
  bookedHours,
  possibleHours,
  projectHours = 0,
  showProjectBreakdown = false,
  "data-testid": testId,
}) => {
  // Calculate percentage
  const percentage = possibleHours > 0 ? bookedHours / possibleHours : 0;
  const percentageDisplay = `${Math.round(percentage * 100)}%`;

  // Calculate available hours
  const availableHours = possibleHours - bookedHours;

  // Calculate hours without project
  const otherHours = showProjectBreakdown
    ? bookedHours - projectHours
    : bookedHours;

  // Determine color state based on percentage
  const getColorState = (): ColorState => {
    if (percentage === 0) return "grey";
    if (percentage > 1) return "red";
    if (percentage >= 0.72) return "green";
    return "orange";
  };

  const colorState = getColorState();

  // Calculate bar widths as percentages
  // When overbooked, scale bars relative to total booked hours for accurate proportions
  const isOverbooked = availableHours < 0;
  const barBase = isOverbooked ? bookedHours : possibleHours;

  const otherBarWidth = barBase > 0 ? (otherHours / barBase) * 100 : 0;
  const projectBarWidth = barBase > 0 ? (projectHours / barBase) * 100 : 0;
  const freeBarWidth =
    possibleHours > 0 && !isOverbooked
      ? (availableHours / possibleHours) * 100
      : 0;
  const overflowBarWidth =
    isOverbooked && barBase > 0
      ? (Math.abs(availableHours) / barBase) * 100
      : 0;

  // Aggregate data for tooltip content
  const aggregateData: AggregateData = useMemo(
    () => ({
      bookedHours,
      possibleHours,
      availableHours,
      percentage,
      projectHours,
      otherHours,
    }),
    [
      bookedHours,
      possibleHours,
      availableHours,
      percentage,
      projectHours,
      otherHours,
    ],
  );

  // Prevent clicks on the aggregate row from bubbling up to parent (e.g., accordion toggle)
  // This allows touch users to tap for tooltips without triggering accordion collapse
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={styles.aggregate}
      data-testid={testId}
      onClick={handleClick}
    >
      {/* Stats Row - contains percentage and total for compact mode layout */}
      <div className={styles.statsRow}>
        {/* Percentage Badge - shows full summary on hover */}
        <Tooltip
          content={
            <AggregateTooltipContent data={aggregateData} variant="summary" />
          }
          position="bottom"
        >
          <div
            className={classNames(styles.percent, styles[colorState])}
            data-testid={testId ? `${testId}-percent` : undefined}
          >
            <span className={styles.label}>{percentageDisplay}</span>
          </div>
        </Tooltip>
      </div>

      {/* Progress Bar - single tooltip for entire bar */}
      <Tooltip
        content={
          <AggregateTooltipContent data={aggregateData} variant="summary" />
        }
        position="bottom"
      >
        <div className={styles.barContainer}>
          {otherHours > 0 && (
            <div
              className={classNames(styles.bar, styles.barOther)}
              style={{ flexBasis: `${otherBarWidth}%` }}
            >
              <span className={styles.label}>
                {/* When overbooked, show capacity (not total booked) so user sees "40 T + 8 T = 48 T" */}
                {formatDecimal(isOverbooked ? possibleHours : otherHours)} T
              </span>
            </div>
          )}

          {showProjectBreakdown && projectHours > 0 && (
            <div
              className={classNames(styles.bar, styles.barProject)}
              style={{ flexBasis: `${projectBarWidth}%` }}
            >
              <span className={styles.label}>
                {formatDecimal(projectHours)} T
              </span>
            </div>
          )}

          {availableHours > 0 && (
            <div
              className={classNames(styles.bar, styles.barFree)}
              style={{ flexBasis: `${freeBarWidth}%` }}
            >
              <span className={styles.label}>
                {formatDecimal(availableHours)} T
              </span>
            </div>
          )}

          {availableHours < 0 && (
            <div
              className={classNames(styles.bar, styles.barOverflow)}
              style={{ flexBasis: `${overflowBarWidth}%` }}
            >
              <span className={styles.label}>
                {formatDecimal(Math.abs(availableHours))} T
              </span>
            </div>
          )}
        </div>
      </Tooltip>

      {/* Mini Badge - shown only in very narrow containers, shows full summary */}
      <Tooltip
        content={
          <AggregateTooltipContent data={aggregateData} variant="summary" />
        }
        position="bottom"
      >
        <div className={classNames(styles.miniBadge, styles[colorState])}>
          <span className={styles.label}>
            {formatDecimal(bookedHours)}/{formatDecimal(possibleHours)} T
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

export default AggregateRow;
